type GoogleMoney = {
  units?: string | number | null;
  nanos?: number | null;
  currencyCode?: string | null;
};

type PriceRangeLike = {
  from?: string | number | null;
  to?: string | number | null;
  currency_code?: string | null;
  currencyCode?: string | null;
  startPrice?: GoogleMoney | null;
  endPrice?: GoogleMoney | null;
};

export function formatPriceRange(value: unknown): string {
  if (!value || typeof value !== "object") return "";

  const item = value as PriceRangeLike;
  const from = item.from ?? item.startPrice;
  const to = item.to ?? item.endPrice;
  const fromText = formatAmount(from);
  const toText = formatAmount(to);
  const currency = firstText(
    item.currency_code,
    item.currencyCode,
    moneyCurrency(from),
    moneyCurrency(to),
  );

  let price = "";
  if (fromText && toText) {
    price = `${fromText} - ${toText}`;
  } else if (fromText) {
    price = `от ${fromText}`;
  } else if (toText) {
    price = `до ${toText}`;
  }

  if (!price) return "";

  return [price, currency].filter(Boolean).join(" ");
}

function formatAmount(value: unknown): string | null {
  if (value === null || value === undefined) return null;

  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
  }

  if (typeof value !== "object") return null;

  const money = value as GoogleMoney;
  if (money.units === undefined && money.nanos === undefined) return null;

  const units = String(money.units ?? 0).trim() || "0";
  const nanos = typeof money.nanos === "number" ? money.nanos : 0;
  if (!Number.isFinite(nanos) || nanos === 0) return units;

  const fractional = String(Math.abs(nanos))
    .padStart(9, "0")
    .replace(/0+$/, "");
  const sign = units === "0" && nanos < 0 ? "-" : "";
  return `${sign}${units}.${fractional}`;
}

function moneyCurrency(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  return firstText((value as GoogleMoney).currencyCode);
}

function firstText(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    const text = value?.trim();
    if (text) return text;
  }
  return null;
}
